import { defineStore } from "pinia";
import { AdminUtilityUserEnableDisableAccount } from "../route-names";
import router from "../routes";
import { getErrorMessage } from "@/shared/errorHelper";
import {
    FetchState,
    LinkedAccountManagementRequest
} from "@/shared/types";
import {
    AccountEnableDisableRequest,
    CognitoAccountEnableDisableResult,
    FileProcessingState,
    FinicityLinkedAccount,
    MfaAdminDisableRequest,
    ResetUserCustodianRequest,
    TestUser,
    UmbReopenAccountRequest,
    UserDobNameModel,
    UtilityState
} from "../types";
import * as storeHelper from "@/shared/storeHelper";
import {
    changeUserEmail,
    disableUserAccount,
    enableUserAccount,
    getTestUsers,
    requestDisableMfa,
    resetPassword,
    resetUserCustodian,
    saveAdminDobNameChange
} from "@/shared/api/userService";
import {
    getTransactionCodeExportFile,
    getTransactionCodeResultFile,
    getTransactionCodeResultFileExists,
    postTransactionCodeFile,
    reopenUmbAccount,
    updateLinkedBankAccount,
    postCpfBlackout,
    getCpfBlackoutDates,
    deleteCpfBlackoutDate
} from "@/shared/api/bankService";
import {
    getAdminFinicityLinkedAccounts
} from "@/shared/api/expenseService";
import _ from "lodash";
import { downloadCsvFile } from "@/shared/fileDownload";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: UtilityState = {
    fetchState: FetchState.Uninitialized,
    testUsersFetchState: FetchState.Uninitialized,
    testUsers: null,
    finicityLinkedAccountsFetchState: FetchState.Uninitialized,
    finicityLinkedAccounts: null,
    transactionCodeUploadId: null,
    transactionCodeProcessingState: FileProcessingState.Uninitialized,
    transactionCodeExportFetchState: FetchState.Uninitialized,
    cpfBlackoutDates: null,
    cpfBlackoutDatesFetchState: FetchState.Uninitialized
};

export const useUtilityStore = defineStore("utility", {
    state: (): UtilityState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        testUsersLoaded: state => state.testUsersFetchState === FetchState.Loaded,
        isFinicityAccountDetailsLoaded: state => state.finicityLinkedAccountsFetchState === FetchState.Loaded,
        finicityAccountDetails: state => state.finicityLinkedAccounts,
        isTransactionCodeFileProcessingComplete: state => state.transactionCodeProcessingState ===
            FileProcessingState.Complete,
        isTransactionCodeFileProcessing: state => state.transactionCodeProcessingState ===
            FileProcessingState.Processing,
        isTransactionCodeExportLoading: state => state.transactionCodeExportFetchState === FetchState.Loading,
        isCpfBlackoutDatesLoaded: state => state.cpfBlackoutDatesFetchState === FetchState.Loaded
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setTestUsersFetchState (fetchState: FetchState) {
            this.testUsersFetchState = fetchState;
        },
        setTestUsers (testUsers: TestUser[]) {
            this.testUsers = testUsers;
        },
        setFinicityLinkedAccountsFetchState (fetchState: FetchState) {
            this.finicityLinkedAccountsFetchState = fetchState;
        },
        setFinicityLinkedAccounts (details: FinicityLinkedAccount[]) {
            this.finicityLinkedAccounts = details;
        },
        setTransactionCodeUploadId (id: string) {
            this.transactionCodeUploadId = id;
        },
        setTransactionCodeProcessingState (processingState: FileProcessingState) {
            this.transactionCodeProcessingState = processingState;
        },
        setTransactionCodeExportFetchState (fetchState: FetchState) {
            this.transactionCodeExportFetchState = fetchState;
        },
        enableAccount (enableAccount: AccountEnableDisableRequest): Promise<CognitoAccountEnableDisableResult> {
            return enableUserAccount(enableAccount).then(response => {
                    return response.data;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        disableAccount (disableAccount: AccountEnableDisableRequest): Promise<CognitoAccountEnableDisableResult> {
            return disableUserAccount(disableAccount).then(response => {
                    return response.data;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        requestDisableMfa (mfaResetRequest: MfaAdminDisableRequest): Promise<any> {
            return requestDisableMfa(mfaResetRequest)
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        adminEmailChange ({ fromEmail, toEmail }: { fromEmail: string, toEmail: string }): Promise<void> {
            const notifierStore = useNotifierStore();
            return changeUserEmail(fromEmail, toEmail)
                .then(() => {
                    notifierStore.setSuccess("Verification email sent.");
                })
                .catch(error => {
                    const errorMessage = getErrorMessage(error);

                    if (errorMessage.toLowerCase().includes("deactivate")) {
                        const route = router.resolve({ name: AdminUtilityUserEnableDisableAccount });
                        const link = `<a href="${route.href}" target="_blank">deactivate</a>`;
                        const message = errorMessage.replace("deactivate", link);
                        notifierStore.setDanger(message);
                    } else {
                        storeHelper.handleError({ error, rethrow: true });
                    }
                });
        },
        saveAdminDobNameChange (userDobName: UserDobNameModel) {
            return saveAdminDobNameChange(userDobName)
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        updateLinkedBankAccount (request: LinkedAccountManagementRequest) {
            const notifierStore = useNotifierStore();
            return updateLinkedBankAccount(request)
                .then(response => {
                    notifierStore.setSuccess(response.data.outcome);
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        reopenUmbAccount (request: UmbReopenAccountRequest) {
            return reopenUmbAccount(request)
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        resetUserCustodian (request: ResetUserCustodianRequest): Promise<boolean> {
            const notifierStore = useNotifierStore();
            return resetUserCustodian(request)
                .then(response => {
                    notifierStore.setSuccess("User's custodian reset.");
                    return true;
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                    return false;
                });
        },
        resetPassword (emailAddress: string) {
            const notifierStore = useNotifierStore();
            return resetPassword(emailAddress)
                .then(response => {
                    notifierStore.setSuccess("User's password reset.");
                    return response.data;
                })
                .catch(error => {
                    notifierStore.setDanger("Unable to reset user's password");
                    return false;
                });
        },
        loadTestUsers () {
            this.setTestUsersFetchState(FetchState.Loading);
            return getTestUsers().then(resp => {
                this.setTestUsers(resp.data);
                this.setTestUsersFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadFinicityLinkedAccounts (cognitoUsername: string) {
            this.setFinicityLinkedAccountsFetchState(FetchState.Loading);
            return getAdminFinicityLinkedAccounts(cognitoUsername).then(resp => {
                this.setFinicityLinkedAccounts(resp.data);
                this.setFinicityLinkedAccountsFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        uploadTransactionCodeFile ({ file } = undefined) {
            return postTransactionCodeFile(file).then(resp => {
                this.setTransactionCodeUploadId(resp.data);
                this.setTransactionCodeProcessingState(FileProcessingState.Processing);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        downloadTransactionCodeResultFile () {
            return getTransactionCodeResultFile(this.transactionCodeUploadId).then(response => {
                downloadCsvFile(response.data, "result.csv");
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        downloadTransactionCodeExportFile () {
            this.setTransactionCodeExportFetchState(FetchState.Loading);
            return getTransactionCodeExportFile().then(response => {
                downloadCsvFile(response.data, "export.csv");
                this.setTransactionCodeExportFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                this.setTransactionCodeExportFetchState(FetchState.Uninitialized);
            });
        },
        getTransactionCodeResultFileExists () {
            return getTransactionCodeResultFileExists(this.transactionCodeUploadId).then(resp => {
                if (resp.data) {
                    this.setTransactionCodeProcessingState(FileProcessingState.Complete);
                }
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getCpfBlackoutDates () {
            if (this.cpfBlackoutDatesFetchState === FetchState.Loading) {
                return;
            }
            this.cpfBlackoutDatesFetchState = FetchState.Loading;

            return getCpfBlackoutDates().then(resp => {
                this.cpfBlackoutDates = resp.data;
                this.cpfBlackoutDatesFetchState = FetchState.Loaded;
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        saveCpfBlackoutDate (date: string) {
            return postCpfBlackout(date)
            .then(() => {
                    this.cpfBlackoutDates.push(date);
                    this.cpfBlackoutDates = this.cpfBlackoutDates.sort();
                })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        deleteCpfBlackoutDate (date: string) {
            return deleteCpfBlackoutDate(date)
            .then(() => {
                    this.cpfBlackoutDates = this.cpfBlackoutDates.filter(f => f !== date);
                })
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        }
    }
});
